import {TYPE_ISSUES, TYPE_QUALITY_ISSUES} from '../../constants/issue-types';
import {getAllIssueWrappersSorted, getProjectId} from '../selectors';
import {ENV_CONFIGS} from '../../constants/envs';
import {updateUrlWithQueryParams} from '../../utils/query-helpers';
import {batchClearPushpinAction, batchCreatePushpinAction} from './pushpin-actions';

export * from './attachment-actions';
export * from './configuration-actions';
export * from './issues-actions';
export * from '../templates/templates-actions';
export * from './pushpin-actions';
export * from '../issuePermissions/issuesPermissions-actions';
export * from './issueTypes-actions';
export * from './requiredFields-actions';
export * from './issueAttributes-actions';
export * from './lbs-actions';
export * from './reports-actions';
export * from './analytics-actions';
export * from './issueRootCauses-actions';
export * from './issueDocuments-actions';
export * from './issueChecklist-actions';

export const SET_FF_LOADED = 'SET_FF_LOADED';
export const ACCESS_TOKEN_SET = 'ACCESS_TOKEN_SET';
export const TARGET_URN_SET = 'TARGET_URN_SET';
export const FILTER_CALLBACK_SET = 'FILTER_CALLBACK_SET';
//export const LOCATION_CHANGE = '@@router/LOCATION_CHANGE';
export const CURRENT_USER_FETCH_START = 'CURRENT_USER_FETCH_START';
export const CURRENT_USER_FETCH_API = 'CURRENT_USER_FETCH_API';
export const CURRENT_USER_FETCH_API_PENDING = 'CURRENT_USER_FETCH_API_PENDING';
export const CURRENT_USER_FETCH_API_FULFILLED = 'CURRENT_USER_FETCH_API_FULFILLED';
export const CURRENT_USER_FETCH_API_REJECTED = 'CURRENT_USER_FETCH_API_REJECTED';
export const CURRENT_DOCS_PROJECT_FETCH_START = 'CURRENT_DOCS_PROJECT_FETCH_START';
export const CURRENT_DOCS_PROJECT_FETCH_FINISHED = 'CURRENT_DOCS_PROJECT_FETCH_FINISHED';
export const CURRENT_DOCS_PROJECT_FETCH_FAILED = 'CURRENT_DOCS_PROJECT_FETCH_FAILED';
export const SET_CURRENT_PROJECT = 'SET_CURRENT_PROJECT';
export const QUERY_PARAMS_SET = 'QUERY_PARAMS_SET';
export const SET_QUERY_PARAMS_FROM_URL = 'SET_QUERY_PARAMS_FROM_URL';
export const SET_FEATURE_FLAG = 'SET_FEATURE_FLAG';
export const SAVE_ISSUE = 'SAVE_ISSUE';
export const SHOW_NOTIFICATION = 'SHOW_NOTIFICATION';
export const RESET_NOTIFICATION = 'RESET_NOTIFICATION';
export const ROUTE_CHANGE = 'ROUTE_CHANGE';
export const HANDLE_NAVIGATE_TO_ISSUE_IN_DOCUMENT = 'HANDLE_NAVIGATE_TO_ISSUE_IN_DOCUMENT';
export const SHEET_CHANGE = 'SHEET_CHANGE';
export const SET_CURRENT_VIEW = 'SET_CURRENT_VIEW';
export const TOKEN_EXPIRED = 'TOKEN_EXPIRED';
export const OPEN_PUSHPIN_SELECTION = 'OPEN_PUSHPIN_SELECTION';
export const CLOSE_PUSHPIN_SELECTION = 'CLOSE_PUSHPIN_SELECTION';
export const CLOSE_ISSUE_DETAILS_MODAL_VIEW = 'CLOSE_ISSUE_DETAILS_MODAL_VIEW';
export const SCROLL_DOWN = 'SCROLL_DOWN';
export const SET_PACKAGE_DEEPLINKING_FALSE = 'SET_PACKAGE_DEEPLINKING_FALSE';
export const PERMISSIONS_VIOLATION_FOUND = 'PERMISSIONS_VIOLATION_FOUND';
export const OPEN_IFRAME = 'OPEN_IFRAME';
export const CLOSE_IFRAME = 'CLOSE_IFRAME';
export const SET_IS_CREATING_ISSUE = 'SET_IS_CREATING_ISSUE';
export const SET_DELETED_USERS = 'SET_DELETED_USERS';
export const UPDATE_DELETED_USERS = 'UPDATE_DELETED_USERS';
export const CHECKLIST_FETCH = 'CHECKLIST_FETCH';
/**
 * These are action-creators -- they return an action, for the store to dispatch:
 */

export const setFFLoaded = () => ({
  type: SET_FF_LOADED
});

export const setAccessTokenAction = token => ({
  type: ACCESS_TOKEN_SET,
  payload: token
});

export const setTargetUrnAction = targetUrn => ({
  type: TARGET_URN_SET,
  payload: targetUrn
});

export const setFilterCallbackAction = filterCallback => (dispatch, getState) => {
  dispatch({
    type: FILTER_CALLBACK_SET,
    payload: filterCallback
  });

  const issues = getAllIssueWrappersSorted(getState()).map(issueWrapper => issueWrapper.attributes);
  if (issues.length > 0) {
    dispatch(batchClearPushpinAction(issues));
    dispatch(batchCreatePushpinAction(issues));
  }
};

export const changeLocationAction = route => (dispatch, getState, {appHistory}) => appHistory.push(route);

export const fetchCurrentUserAction = () => ({
  type: CURRENT_USER_FETCH_START
});

export const setCurrentProject = currentProject => ({
  type: SET_CURRENT_PROJECT,
  payload: currentProject
});

export const fetchCurrentDocsProjectAction = () => (dispatch, getState, {docsSDK}) => {
  dispatch({type: CURRENT_DOCS_PROJECT_FETCH_START});
  docsSDK
    .getProjectById(getProjectId(getState()))
    .then(project => {
      dispatch({
        type: CURRENT_DOCS_PROJECT_FETCH_FINISHED,
        payload: project
      });
    })
    .catch(error => {
      dispatch({
        type: CURRENT_DOCS_PROJECT_FETCH_FAILED,
        payload: error
      });
    });
};

/**
 * Set query params and update the query.
 * This is a thunk action so that it only gets executed after it is dispatched.
 * @param {Object} queryParams
 * @param {String} collectionType
 */
export const setQueryParamsAction = (queryParams, collectionType) => dispatch => {
  const clonedQueryParams = {...queryParams};
  dispatch({
    type: QUERY_PARAMS_SET,
    payload: {
      queryParams: clonedQueryParams,
      urlUpdateResult: updateUrlWithQueryParams(clonedQueryParams)
    },
    meta: {
      collectionType
    }
  });
};

export const setQueryParamsFromUrlAction = (queryParams, issueType, currentView, newLocation, collectionType) => ({
  type: SET_QUERY_PARAMS_FROM_URL,
  payload: {queryParams, issueType, currentView, newLocation},
  meta: {
    collectionType
  }
});

export const setFeatureFlagAction = featureFlag => ({
  type: SET_FEATURE_FLAG,
  payload: featureFlag
});

export const showNotificationAction = notification => ({
  type: SHOW_NOTIFICATION,
  payload: notification
});

export const resetNotification = () => ({
  type: RESET_NOTIFICATION
});

export const routeChangeEventTriggerAction = (pathname, params) => {
  // Override all "quality_issues" details with "issues"
  // when communicating with the hosting app
  let manipulatedPathname = pathname;
  const manipulatedParams = Object.assign({}, params);
  if (params.issueType === TYPE_QUALITY_ISSUES) {
    manipulatedParams.issueType = TYPE_ISSUES;
    manipulatedPathname = manipulatedPathname.replace(`/${TYPE_QUALITY_ISSUES}`, `/${TYPE_ISSUES}`);
  }

  return {
    type: ROUTE_CHANGE,
    payload: {pathname: manipulatedPathname, params: manipulatedParams}
  };
};

export const openIframe = (url, title) => ({
  type: OPEN_IFRAME,
  payload: {url, title}
});

export const closeIframe = () => ({
  type: CLOSE_IFRAME
});

export const navigateToIssueInDocumentAction = (targetUrn, documentName, issueType, issueId, location) => (
  dispatch,
  getState,
  {redispatcher}
) => {
  if (redispatcher?.listenerCount(HANDLE_NAVIGATE_TO_ISSUE_IN_DOCUMENT) > 0) {
    dispatch({
      type: HANDLE_NAVIGATE_TO_ISSUE_IN_DOCUMENT,
      payload: {targetUrn, documentName, issueType, issueId, location}
    });
  } else {
    const projectId = getProjectId(getState());
    const baseUrl = `${ENV_CONFIGS.DOCS}/projects/${projectId}`;
    const urlQueryParams = `panels=default&layouts=disableTitleBar&requestDomain=${window.location.origin}`;
    const url = `${baseUrl}/viewer/items/${targetUrn}/issues/${issueId}?${urlQueryParams}`;

    dispatch(openIframe(url, documentName));
  }
};

export const sheetChangeAction = newSheetGuid => ({
  type: SHEET_CHANGE,
  payload: newSheetGuid
});

export const setCurrentViewAction = view => ({
  type: SET_CURRENT_VIEW,
  payload: view
});

export const tokenExpired = () => ({
  type: TOKEN_EXPIRED
});

export const openPushpinSelection = (document, status) => ({
  type: OPEN_PUSHPIN_SELECTION,
  payload: {
    document,
    status
  }
});

export const closePushpinSelection = () => ({
  type: CLOSE_PUSHPIN_SELECTION
});

export const closeIssueDetailsModalViewEventTriggerAction = () => ({
  type: CLOSE_ISSUE_DETAILS_MODAL_VIEW
});

export const scrollDownAction = (offset, speed) => ({
  type: SCROLL_DOWN,
  payload: {
    offset,
    speed
  }
});

export const permissionsViolation = config => ({
  type: PERMISSIONS_VIOLATION_FOUND,
  payload: {config}
});

export const setPackageDeepLinkingFalse = () => ({type: SET_PACKAGE_DEEPLINKING_FALSE});

export const setIsCreatingIssueAction = isCreatingIssue => ({type: SET_IS_CREATING_ISSUE, payload: isCreatingIssue});

export const setDeletedUsers = data => ({
  type: SET_DELETED_USERS,
  payload: data
});

export const updateDeletedUsers = data => ({
  type: UPDATE_DELETED_USERS,
  payload: data.results
});
